import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileSpreadsheet, FileAudio, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDataExport() {
  const { toast } = useToast();
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [justDownloaded, setJustDownloaded] = useState(false);
  const [downloadingAudio, setDownloadingAudio] = useState(false);
  const [justDownloadedAudio, setJustDownloadedAudio] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  });

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/data-export/summary`, getAuthHeaders());
        setSummary(data);
      } catch (err) {
        setSummary(null);
      } finally {
        setLoadingSummary(false);
      }
    };
    loadSummary();
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    setJustDownloaded(false);
    try {
      const response = await axios.get(`${API_URL}/api/admin/data-export/excel`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Sanjaya_Data_Export_${today}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      setJustDownloaded(true);
      toast({ title: 'Download started', description: 'Your Excel file is downloading now.' });
    } catch (err) {
      toast({
        title: 'Download failed',
        description: 'Something went wrong while preparing the file. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAudio = async () => {
    setDownloadingAudio(true);
    setJustDownloadedAudio(false);
    try {
      const response = await axios.get(`${API_URL}/api/admin/data-export/audio-zip`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Sanjaya_Audio_Export_${today}.zip`;
      link.click();
      URL.revokeObjectURL(url);
      setJustDownloadedAudio(true);
      toast({ title: 'Download started', description: 'Your ZIP of audio recordings is downloading now.' });
    } catch (err) {
      const noAudio = err?.response?.status === 404;
      toast({
        title: noAudio ? 'No recordings found' : 'Download failed',
        description: noAudio
          ? 'There are no saved audio recordings to export yet.'
          : 'Something went wrong while preparing the ZIP. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingAudio(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start" data-testid="admin-data-export-root">
      <Card className="border-2 border-sanjaya-navy/10 shadow-sm h-full flex flex-col">
        <CardHeader>
          <div className="w-14 h-14 rounded-xl bg-sanjaya-navy/10 flex items-center justify-center mb-3">
            <FileSpreadsheet className="w-7 h-7 text-sanjaya-navy" />
          </div>
          <CardTitle className="text-2xl">Data Export</CardTitle>
          <CardDescription className="text-base">
            Download every report on the platform as one Excel file — including the full
            session transcription, and the names of the child, observer, and principal
            for each report.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col flex-1 space-y-6">
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
            {loadingSummary ? (
              <span className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking how much data is available…
              </span>
            ) : summary ? (
              <p>
                <span className="font-semibold">{summary.total_reports}</span> reports are on record,{' '}
                <span className="font-semibold">{summary.reports_with_transcript}</span> of which
                include a transcription, across{' '}
                <span className="font-semibold">{summary.total_children}</span> children.
              </p>
            ) : (
              <span className="text-red-500">Could not load the report count. You can still try exporting below.</span>
            )}
          </div>

          <Button
            onClick={handleDownload}
            disabled={downloading}
            size="lg"
            className="w-full min-h-[56px] text-base bg-sanjaya-navy hover:bg-blue-900 text-white gap-2"
          >
            {downloading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Preparing your file…
              </>
            ) : justDownloaded ? (
              <>
                <CheckCircle2 className="w-5 h-5" /> Downloaded — Click to Export Again
              </>
            ) : (
              <>
                <Download className="w-5 h-5" /> Export All Data (Excel)
              </>
            )}
          </Button>

          <p className="text-xs text-gray-400 text-center mt-auto">
            The file opens directly in Excel, Google Sheets, or Numbers. No setup needed.
          </p>
        </CardContent>
      </Card>

      <Card className="border-2 border-sanjaya-teal/10 shadow-sm h-full flex flex-col">
        <CardHeader>
          <div className="w-14 h-14 rounded-xl bg-sanjaya-teal/10 flex items-center justify-center mb-3">
            <FileAudio className="w-7 h-7 text-sanjaya-teal" />
          </div>
          <CardTitle className="text-2xl">Audio Export</CardTitle>
          <CardDescription className="text-base">
            Download every saved report recording as one ZIP file, along with an index
            spreadsheet naming the child, observer, and principal for each recording.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col flex-1 space-y-6">
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
            {loadingSummary ? (
              <span className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking how many recordings are available…
              </span>
            ) : summary ? (
              <p>
                <span className="font-semibold">{summary.reports_with_audio}</span> saved audio
                recording{summary.reports_with_audio === 1 ? '' : 's'} ready to export.
              </p>
            ) : (
              <span className="text-red-500">Could not load the recording count. You can still try exporting below.</span>
            )}
          </div>

          <Button
            onClick={handleDownloadAudio}
            disabled={downloadingAudio || (summary && summary.reports_with_audio === 0)}
            size="lg"
            className="w-full min-h-[56px] text-base bg-sanjaya-teal hover:bg-emerald-800 text-white gap-2"
          >
            {downloadingAudio ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Zipping up recordings — this may take a moment…
              </>
            ) : justDownloadedAudio ? (
              <>
                <CheckCircle2 className="w-5 h-5" /> Downloaded — Click to Export Again
              </>
            ) : (
              <>
                <Download className="w-5 h-5" /> Export Audio Recordings (ZIP)
              </>
            )}
          </Button>

          <p className="text-xs text-gray-400 text-center mt-auto">
            Recordings can add up to a large file — keep this tab open until the download starts.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
